import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import qs from 'query-string';
import { useMutation, useApolloClient } from 'react-apollo';
import omit from 'lodash.omit';
import { useSelector } from 'react-redux';
import { singletonHook } from 'react-singleton-hook';

import { accountReferralCreate as accountReferralCreateMutation } from '../../graphql/mutations/account-referrals';
import { ACCOUNT_REFERRAL_TYPE } from '../../constants/account-referrals';
import getLogger from '../../util/logger';
import { AccountAffiliateUrlVerify } from '../../graphql/queries/account-referrals';

const log = getLogger('useStoreReferralAttributes');

/**
 * Manages referral / affiliate attributes as a whole.
 *
 * Checks for rewards (Affiliate URLs only), as well the validity of Referral urls.
 *
 * @returns {{accountReferralCreate: ((function(*=, *=): Promise<void>)|*), referralAttributes: {}, referralAttributesParsed: boolean, setIsEligibleForReward: (value: (((prevState: boolean) => boolean) | boolean)) => void, isEligibleForReward: boolean, hasActiveReferralAttribute: (boolean|*), loading: boolean, user: *}}
 */

const useStoreReferralAttributes = () => {
  const location = useLocation();
  const authenticated = useSelector(({ auth }) => auth.authenticated);
  const [isEligibleForReward, setIsEligibleForReward] = useState(false);
  const [loading, setLoading] = useState(false);
  const client = useApolloClient();
  const [referralAttributesParsed, setReferralAttributesParsed] =
    useState(false);

  const [createAccountReferral] = useMutation(accountReferralCreateMutation);
  // Mutation will not rely on this value. Since this is a singleton hook that is rendered
  // At the top level of the app, no matter where  the method ends up being called, we will
  // have these values available.
  const [referralAttributes, setReferralAttributes] = useState({});

  const hasActiveReferralAttribute = referralAttributesCurrent => {
    if (!referralAttributesCurrent) {
      return false;
    }

    const { ref_id: refId, origin } = referralAttributesCurrent || {};

    return refId || origin;
  };

  /**
   * Finds and stores into apollo store referral attributes.
   *
   * @param {String} search Search query string to look for attributes in.
   * @returns {Promise<boolean>} Whether or not referralAttributes were found.
   */
  const parseReferralAttributes = async search => {
    setLoading(true);

    const { ref_id: refId, origin, utm_source } = qs.parse(search);
    const utmOrigin =
      typeof utm_source === 'string'
        ? `UTM_SOURCE_${utm_source.replace(' ', '_').trim()}`
        : '';

    const referralAttributesCurrent = {
      ref_id: refId ?? '',
      origin: origin ?? utmOrigin ?? '',
    };

    if (refId && authenticated) {
      // check if it is valid:
      try {
        const {
          data: {
            accountAffiliateUrlVerify: {
              is_valid: isValid,
              is_eligible: isEligible,
              type,
            },
          } = {},
        } = await client.query({
          query: AccountAffiliateUrlVerify,
          variables: {
            refId,
          },
        });

        if (!isValid) {
          setLoading(false);
          return false;
        }

        log.info(
          'parseReferralAttributes :: refId is valid for type',
          type,
          'and this user is eligible: '.isEligible,
        );

        setIsEligibleForReward(isEligible);
      } catch (error) {
        log.error('parseReferralAttributes ::', error);
        setLoading(false);
        return false;
      }
    }

    if (!hasActiveReferralAttribute(referralAttributesCurrent)) {
      setLoading(false);
      return false;
    }

    log.info(
      'parseReferralAttributes :: storing the following referral attributes',
      referralAttributesCurrent,
    );

    setReferralAttributes(referralAttributesCurrent);
    setLoading(false);
    return true;
  };

  const accountReferralCreate = async (
    accountReferralType = ACCOUNT_REFERRAL_TYPE.ACCOUNT_SIGNUP,
    clearReferralData = false,
  ) => {
    try {
      if (!hasActiveReferralAttribute(referralAttributes)) {
        // Cannot create a referral without values stored.
        log.info(
          'accountReferralCreate :: Nothing done. No referral attributes found...',
        );
        return;
      }

      log.info('accountReferralCreate :: Calling createAccountReferral...');

      await createAccountReferral({
        variables: {
          input: {
            ...omit(referralAttributes, ['__typename']),
            referral_type: referralAttributes.ref_id
              ? undefined
              : accountReferralType,
          },
        },
      });

      if (clearReferralData) {
        setReferralAttributes({});
      }
    } catch (err) {
      // No need to report this to the end-user.
      // Referee does not care whether they used the
      // ref. id or not.
      log.error('accountReferralCreate :: ', err);
    }
  };

  const parseReferralAttributesFromQuery = async () => {
    const { next } = qs.parse(location.search);
    const baseReferralAttributesValid = await parseReferralAttributes(
      location.search,
    );

    if (!baseReferralAttributesValid && next) {
      const nextQueryString = `?${next.split('?')[1] ?? ''}`;
      await parseReferralAttributes(nextQueryString);
    }

    setReferralAttributesParsed(true);
  };

  useEffect(() => {
    parseReferralAttributesFromQuery();
  }, [location.search]);

  return {
    loading,
    referralAttributesParsed,
    accountReferralCreate,
    referralAttributes,
    hasActiveReferralAttribute: hasActiveReferralAttribute(referralAttributes),
    isEligibleForReward,
    setIsEligibleForReward,
  };
};

export { useStoreReferralAttributes as useStoreReferralAttributesHook };

export default singletonHook({}, useStoreReferralAttributes);
